// src/pages/SalesPersonCost.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { BaseUrl } from "../BaseUrl";

const API_URL = `${BaseUrl}/api/products`;

const LS_VISIBLE_COLUMNS = "sales_person_cost_visible_columns_v1";

const UI = {
  pageBg: "#EAEDED",
  headerBg: "#131921",
  orange: "#F08804",
  orange2: "#FF9900",
  blue: "#007185",
  green: "#007600",
  red: "#B12704",
  text: "#111827",
  sub: "#565959",
  border: "#D5D9D9",
  card: "#FFFFFF",
};

const ALL_COLUMNS = [
  { key: "category", label: "Category", type: "text", group: "Product" },
  { key: "subCategory", label: "Sub Category", type: "text", group: "Product" },
  { key: "companyName", label: "Company Name", type: "text", group: "Product" },
  {
    key: "boardQuality",
    label: "Board Quality",
    type: "text",
    group: "Product",
  },
  { key: "name", label: "Name", type: "text", group: "Product" },

  { key: "lcm", label: "Lcm", type: "number", group: "Product" },
  { key: "bcm", label: "Bcm", type: "number", group: "Product" },
  { key: "wtPer100", label: "Wt / 100", type: "number", group: "Product" },
  { key: "gsm", label: "GSM", type: "number", group: "Product" },
  { key: "bagQnty", label: "Bag Qnty", type: "number", group: "Product" },
  { key: "bagWeight", label: "Bag Weight", type: "number", group: "Product" },

  { key: "pcsBreadth", label: "Pcs Breadth", type: "number", group: "Order" },
  { key: "pcsLength", label: "Pcs Length", type: "number", group: "Order" },
  {
    key: "piecePerSheet",
    label: "Piece / Sheet",
    type: "number",
    group: "Order",
  },
  {
    key: "orderSheetB",
    label: "Order Sheet B",
    type: "number",
    group: "Order",
  },
  {
    key: "orderSheetL",
    label: "Order Sheet L",
    type: "number",
    group: "Order",
  },
  {
    key: "orderSheetGsm",
    label: "Order Sheet GSM",
    type: "number",
    group: "Order",
  },
  { key: "quantity", label: "Quantity", type: "number", group: "Order" },
  { key: "totalPcs", label: "Total Pcs", type: "number", group: "Order" },
  { key: "totalWeight", label: "Total Weight", type: "number", group: "Order" },
  { key: "rate", label: "Rate", type: "number", group: "Order" },
  { key: "amount", label: "Amount", type: "money", group: "Order" },
  { key: "gstAmount", label: "GST Amount", type: "money", group: "Order" },
  { key: "totalAmount", label: "Total Amount", type: "money", group: "Order" },
  {
    key: "pricePerPiece",
    label: "Price / Piece",
    type: "money",
    group: "Order",
  },

  {
    key: "printingCharges",
    label: "Printing Charges",
    type: "money",
    group: "Costing",
  },
  {
    key: "plateCharge",
    label: "Plate Charge",
    type: "money",
    group: "Costing",
  },
  {
    key: "cuttingCharges",
    label: "Cutting Charges",
    type: "money",
    group: "Costing",
  },
  {
    key: "conversionCost",
    label: "Conversion Cost",
    type: "money",
    group: "Costing",
  },
  { key: "miscCost", label: "Misc Cost", type: "money", group: "Costing" },
  { key: "totalCost", label: "Total Cost", type: "money", group: "Costing" },
  { key: "costPerPcs", label: "Cost / Pcs", type: "money", group: "Costing" },
  { key: "salePrice", label: "Sale Price", type: "money", group: "Costing" },
  {
    key: "profitPerPiece",
    label: "Profit / Piece",
    type: "money",
    group: "Costing",
  },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "category",
  "subCategory",
  "boardQuality",
  "name",
  "gsm",
  "orderSheetL",
  "orderSheetB",
  "orderSheetGsm",
  "quantity",
  "totalWeight",
  "rate",
  "amount",
  "gstAmount",
  "totalAmount",
  "salePrice",
  "totalCost",
  "profitPerPiece",
];

function safeStr(v) {
  return (v ?? "").toString();
}

function norm(v) {
  return safeStr(v).trim().toLowerCase();
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function fmt(v, type = "text") {
  if (v === null || v === undefined || v === "") return "—";

  if (type === "money") {
    return num(v).toFixed(2);
  }

  if (type === "number") {
    return num(v).toFixed(2);
  }

  return safeStr(v);
}

function readLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function normalizeProduct(p) {
  const category = safeStr(p?.category || p?.catageory).trim();

  const subCategory = safeStr(
    p?.subCategory ||
      p?.subcategory ||
      p?.subCatageory ||
      p?.subcatageory ||
      p?.sub_catageory,
  ).trim();

  return {
    ...p,
    category,
    subCategory,

    gstAmount: p?.gstAmount ?? p?.gstRate ?? "",
    printingCharges: p?.printingCharges ?? p?.printingPer1000 ?? "",
    cuttingCharges: p?.cuttingCharges ?? p?.cuttingPer1000 ?? "",

    totalAmount: p?.totalAmount ?? "",
    pricePerPiece: p?.pricePerPiece ?? "",
    profitPerPiece: p?.profitPerPiece ?? "",
  };
}

function makeTotals(rows, visibleColumns) {
  const totals = {};

  visibleColumns.forEach((colKey) => {
    const col = ALL_COLUMNS.find((x) => x.key === colKey);
    if (!col || col.type === "text") return;

    totals[colKey] = rows.reduce((sum, row) => sum + num(row?.[colKey]), 0);
  });

  return totals;
}

function getColumnWidth(col) {
  if (!col) return 12;

  if (col.key === "name") return 28;
  if (col.key === "companyName") return 24;
  if (col.key === "category" || col.key === "subCategory") return 18;
  if (col.key === "boardQuality") return 18;

  return 14;
}

function ColumnChooserDialog({
  open,
  onClose,
  visibleColumns,
  setVisibleColumns,
}) {
  const [columnSearch, setColumnSearch] = useState("");

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        return prev.filter((x) => x !== key);
      }
      return [...prev, key];
    });
  };

  const selectGroup = (group) => {
    const groupKeys = ALL_COLUMNS.filter((c) => c.group === group).map(
      (c) => c.key,
    );
    setVisibleColumns(groupKeys);
  };

  const selectAll = () => {
    setVisibleColumns(ALL_COLUMNS.map((c) => c.key));
  };

  const resetDefault = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  const clearAll = () => {
    setVisibleColumns([]);
  };

  const filteredColumns = useMemo(() => {
    const q = norm(columnSearch);
    if (!q) return ALL_COLUMNS;

    return ALL_COLUMNS.filter(
      (c) => norm(c.label).includes(q) || norm(c.group).includes(q),
    );
  }, [columnSearch]);

  const groups = ["Product", "Order", "Costing"];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 1000,
          background: UI.headerBg,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        Column Filter
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ background: "linear-gradient(180deg,#F7F8FA,#FFFFFF)" }}
      >
        <Box sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search column name..."
            value={columnSearch}
            onChange={(e) => setColumnSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                background: "#fff",
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={selectAll}
            sx={smallBtnSx}
          >
            Select All
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={resetDefault}
            sx={smallBtnSx}
          >
            Default
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={clearAll}
            sx={smallBtnSx}
          >
            Clear All
          </Button>

          {groups.map((g) => (
            <Button
              key={g}
              size="small"
              variant="contained"
              onClick={() => selectGroup(g)}
              sx={{
                ...smallBtnSx,
                background:
                  g === "Product"
                    ? "#146EB4"
                    : g === "Order"
                      ? "#007600"
                      : "#B12704",
                color: "#fff",
                "&:hover": {
                  background:
                    g === "Product"
                      ? "#0f5c9a"
                      : g === "Order"
                        ? "#005f00"
                        : "#8f1f03",
                },
              }}
            >
              {g}
            </Button>
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 1,
          }}
        >
          {filteredColumns.map((col) => (
            <Box
              key={col.key}
              sx={{
                border: `1px solid ${UI.border}`,
                borderRadius: 2,
                background: "#fff",
                px: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleColumns.includes(col.key)}
                    onChange={() => toggleColumn(col.key)}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                      {col.label}
                    </Typography>
                    <Typography
                      sx={{ color: UI.sub, fontSize: 11, fontWeight: 700 }}
                    >
                      {col.group}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Typography sx={{ color: UI.sub, fontWeight: 800, fontSize: 13 }}>
          Showing {visibleColumns.length} of {ALL_COLUMNS.length} columns
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            background: UI.orange,
            color: "#111",
            fontWeight: 1000,
            "&:hover": { background: UI.orange2 },
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SalesPersonCost() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState(() =>
    readLS(LS_VISIBLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS),
  );

  useEffect(() => {
    writeLS(LS_VISIBLE_COLUMNS, visibleColumns);
  }, [visibleColumns]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const arr = Array.isArray(res.data) ? res.data : [];
      setProducts(arr.map(normalizeProduct));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load products",
      );
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const visibleColumnObjects = useMemo(() => {
    return ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  }, [visibleColumns]);

  const filteredProducts = useMemo(() => {
    const q = norm(searchText);
    if (!q) return products;

    return products.filter((p) =>
      ALL_COLUMNS.some((col) => norm(p?.[col.key]).includes(q)),
    );
  }, [products, searchText]);

  const totals = useMemo(() => {
    return makeTotals(filteredProducts, visibleColumns);
  }, [filteredProducts, visibleColumns]);

  const resetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  const exportExcel = () => {
    if (!visibleColumnObjects.length) {
      alert("Please select at least one column.");
      return;
    }

    if (!filteredProducts.length) {
      alert("No product data available to export.");
      return;
    }

    const excelRows = filteredProducts.map((row, index) => {
      const obj = {
        "S.No": index + 1,
      };

      visibleColumnObjects.forEach((col) => {
        obj[col.label] =
          col.type === "text" ? safeStr(row?.[col.key]) : num(row?.[col.key]);
      });

      return obj;
    });

    const totalRow = {
      "S.No": "TOTAL",
    };

    visibleColumnObjects.forEach((col) => {
      totalRow[col.label] = col.type === "text" ? "" : num(totals?.[col.key]);
    });

    excelRows.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(excelRows);

    ws["!cols"] = [
      { wch: 8 },
      ...visibleColumnObjects.map((col) => ({
        wch: getColumnWidth(col),
      })),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Person Cost");

    XLSX.writeFile(wb, "Sales_Person_Cost.xlsx");
  };

  const exportPDF = () => {
    if (!visibleColumnObjects.length) {
      alert("Please select at least one column.");
      return;
    }

    if (!filteredProducts.length) {
      alert("No product data available to export.");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(14);
    doc.text("Sales Person Cost", 40, 30);

    doc.setFontSize(9);
    doc.text(`Products: ${filteredProducts.length}`, 40, 46);
    doc.text(`Columns: ${visibleColumnObjects.length}`, 140, 46);

    const head = [["S.No", ...visibleColumnObjects.map((col) => col.label)]];

    const body = filteredProducts.map((row, index) => [
      index + 1,
      ...visibleColumnObjects.map((col) =>
        col.type === "text"
          ? safeStr(row?.[col.key])
          : fmt(row?.[col.key], col.type),
      ),
    ]);

    const totalRow = [
      "TOTAL",
      ...visibleColumnObjects.map((col) =>
        col.type === "text" ? "—" : fmt(totals?.[col.key], col.type),
      ),
    ];

    body.push(totalRow);

    const columnCount = visibleColumnObjects.length + 1;

    autoTable(doc, {
      head,
      body,
      startY: 60,
      theme: "grid",
      styles: {
        fontSize: columnCount > 22 ? 4.8 : columnCount > 16 ? 5.6 : 7,
        cellPadding: columnCount > 22 ? 2 : 3,
        overflow: "linebreak",
        valign: "middle",
        lineColor: [210, 210, 210],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [19, 25, 33],
        textColor: 255,
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: 20,
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248],
      },
      didParseCell: (data) => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [19, 25, 33];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 40, left: 15, right: 15 },
    });

    doc.save("Sales_Person_Cost.pdf");
  };

  return (
    <Box sx={{ minHeight: "100vh", background: UI.pageBg }}>
      {/* Header */}
      <Box sx={{ background: UI.headerBg, color: "#fff" }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              py: 1.4,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 1000, fontSize: 20 }}>
                Sales Person Cost
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.72)",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                Product data fetched from Home product master
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Chip
              label={`Products: ${filteredProducts.length}`}
              sx={{
                color: "#fff",
                background: "rgba(255,255,255,0.12)",
                fontWeight: 1000,
              }}
            />

            <Chip
              label={`Columns: ${visibleColumns.length}`}
              sx={{
                color: "#fff",
                background: "rgba(255,255,255,0.12)",
                fontWeight: 1000,
              }}
            />

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchProducts}
              disabled={loading}
              sx={{
                background: "#fff",
                color: "#111",
                fontWeight: 1000,
                "&:hover": { background: "#F0F2F2" },
              }}
            >
              {loading ? "Loading..." : "Refresh"}
            </Button>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportExcel}
              disabled={
                !filteredProducts.length || !visibleColumnObjects.length
              }
              sx={{
                background: UI.green,
                color: "#fff",
                fontWeight: 1000,
                "&:hover": { background: "#005f00" },
              }}
            >
              Excel
            </Button>

            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportPDF}
              disabled={
                !filteredProducts.length || !visibleColumnObjects.length
              }
              sx={{
                background: UI.red,
                color: "#fff",
                fontWeight: 1000,
                "&:hover": { background: "#8f1f03" },
              }}
            >
              PDF
            </Button>

            <Button
              variant="contained"
              startIcon={<ViewColumnIcon />}
              onClick={() => setColumnDialogOpen(true)}
              sx={{
                background: UI.orange,
                color: "#111",
                fontWeight: 1000,
                "&:hover": { background: UI.orange2 },
              }}
            >
              Column Filter
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ py: 2, px: { xs: 1, md: 2 } }}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            overflow: "visible",
            borderColor: UI.border,
            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
            background: UI.card,
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              p: 1.2,
              borderBottom: `1px solid ${UI.border}`,
              background: "linear-gradient(90deg,#FFFFFF,#F7F8FA)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search any product field..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 320 },
                background: "#fff",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
                "& input": {
                  fontWeight: 800,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={() => setSearchText("")}
              sx={{ fontWeight: 1000 }}
            >
              Clear Search
            </Button>

            <Button
              variant="outlined"
              onClick={resetColumns}
              sx={{ fontWeight: 1000 }}
            >
              Reset Columns
            </Button>

            <Box sx={{ flex: 1 }} />

            <Typography sx={{ color: UI.sub, fontWeight: 900, fontSize: 13 }}>
              Excel/PDF export uses visible columns and filtered rows only
            </Typography>
          </Box>

          {visibleColumnObjects.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{ fontWeight: 1000, fontSize: 18, color: UI.text }}
              >
                No columns selected
              </Typography>
              <Typography sx={{ mt: 1, color: UI.sub, fontWeight: 800 }}>
                Click Column Filter and select at least one column.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setColumnDialogOpen(true)}
                sx={{
                  mt: 2,
                  background: UI.orange,
                  color: "#111",
                  fontWeight: 1000,
                  "&:hover": { background: UI.orange2 },
                }}
              >
                Open Column Filter
              </Button>
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{ fontWeight: 1000, fontSize: 18, color: UI.text }}
              >
                No product found
              </Typography>
              <Typography sx={{ mt: 1, color: UI.sub, fontWeight: 800 }}>
                Try clearing search or refresh product data.
              </Typography>
            </Box>
          ) : (
            <Table
              size="small"
              sx={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headSx, width: 48 }}>S.No</TableCell>

                  {visibleColumnObjects.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={headSx}
                      align={col.type === "text" ? "left" : "right"}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredProducts.map((row, index) => (
                  <TableRow
                    key={row?._id || index}
                    sx={{
                      "&:nth-of-type(even)": { background: "#FAFAFA" },
                      "&:hover": { background: "#FFF7ED" },
                    }}
                  >
                    <TableCell sx={bodySx}>{index + 1}</TableCell>

                    {visibleColumnObjects.map((col) => {
                      const isMoney =
                        col.key === "amount" ||
                        col.key === "gstAmount" ||
                        col.key === "totalAmount" ||
                        col.key === "totalCost" ||
                        col.key === "profitPerPiece";

                      return (
                        <TableCell
                          key={col.key}
                          sx={{
                            ...bodySx,
                            color:
                              col.key === "profitPerPiece"
                                ? UI.green
                                : col.key === "totalCost"
                                  ? UI.red
                                  : isMoney
                                    ? UI.blue
                                    : UI.text,
                            fontWeight: [
                              "name",
                              "totalAmount",
                              "totalCost",
                              "profitPerPiece",
                            ].includes(col.key)
                              ? 1100
                              : 900,
                          }}
                          align={col.type === "text" ? "left" : "right"}
                        >
                          {fmt(row?.[col.key], col.type)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

                <TableRow sx={{ background: UI.headerBg }}>
                  <TableCell sx={totalSx}>TOTAL</TableCell>

                  {visibleColumnObjects.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={totalSx}
                      align={col.type === "text" ? "left" : "right"}
                    >
                      {col.type === "text"
                        ? "—"
                        : fmt(totals[col.key], col.type)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>

      <ColumnChooserDialog
        open={columnDialogOpen}
        onClose={() => setColumnDialogOpen(false)}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
      />
    </Box>
  );
}

const smallBtnSx = {
  fontWeight: 1000,
  textTransform: "none",
};

const headSx = {
  background: "#F7F8FA",
  color: UI.text,
  fontWeight: 1100,
  fontSize: 12,
  borderBottom: `1px solid ${UI.border}`,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.15,
  px: 0.7,
  py: 0.8,
};

const bodySx = {
  color: UI.text,
  fontWeight: 900,
  fontSize: 12,
  borderBottom: `1px solid ${UI.border}`,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.2,
  px: 0.7,
  py: 0.75,
};

const totalSx = {
  color: "#fff",
  fontWeight: 1100,
  fontSize: 12,
  borderBottom: "none",
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.2,
  px: 0.7,
  py: 0.9,
};
